import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  keywords?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
}

export const SEO: React.FC<SEOProps> = ({
  title = "Driver Finanças - Controle sua vida financeira",
  description = "Sistema completo de gestão financeira para motoristas de aplicativo. Aumente seu lucro líquido em 30% com controle de ganhos, gastos e manutenção de veículos.",
  image = "/og-image.jpg",
  url = "https://appdriverfinancas.com.br",
  type = "website",
  keywords = "motorista, aplicativo, finanças, controle financeiro, uber, ifood, lucro, gastos, manutenção",
  author = "Driver Finanças",
  publishedTime,
  modifiedTime,
  section,
  tags
}) => {
  const siteName = "Driver Finanças";
  const twitterHandle = "@driverfinancas";

  // Canonical URL
  const canonicalUrl = url.startsWith('http') ? url : `https://appdriverfinancas.com.br${url}`;

  // OG Image URL
  const ogImageUrl = image.startsWith('http') ? image : `https://appdriverfinancas.com.br${image}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="pt_BR" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={ogImageUrl} />
      <meta property="twitter:image:alt" content={title} />
      <meta property="twitter:creator" content={twitterHandle} />
      <meta property="twitter:site" content={twitterHandle} />

      {/* Article specific (if type is article) */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      {type === 'article' && section && (
        <meta property="article:section" content={section} />
      )}
      {type === 'article' && tags && tags.map(tag => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="theme-color" content="#00E185" />
      <meta name="msapplication-TileColor" content="#00E185" />

      {/* Favicon and Icons */}
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/favicon.png" />

      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": type === 'article' ? 'Article' : 'WebSite',
          "name": siteName,
          "url": "https://appdriverfinancas.com.br",
          "description": description,
          "publisher": {
            "@type": "Organization",
            "name": siteName,
            "logo": {
              "@type": "ImageObject",
              "url": "https://appdriverfinancas.com.br/favicon.png"
            }
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://appdriverfinancas.com.br/search?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        })}
      </script>
    </Helmet>
  );
};

// Predefined SEO configs for common pages
export const SEOConfigs = {
  home: {
    title: "Driver Finanças - Controle sua vida financeira",
    description: "Sistema completo de gestão financeira para motoristas de aplicativo. Aumente seu lucro líquido em 30% com controle de ganhos, gastos e manutenção de veículos.",
    url: "/",
    keywords: "motorista, aplicativo, finanças, controle financeiro, uber, ifood, lucro, gastos, manutenção"
  },
  dashboard: {
    title: "Dashboard - Driver Finanças",
    description: "Seu painel de controle financeiro pessoal. Acompanhe ganhos, gastos e metas em tempo real.",
    url: "/dashboard",
    type: "article" as const
  },
  pricing: {
    title: "Planos - Driver Finanças",
    description: "Escolha o plano ideal para maximizar seus lucros. Teste grátis por 7 dias.",
    url: "/pricing",
    keywords: "planos, preços, assinatura, pro, premium"
  },
  login: {
    title: "Login - Driver Finanças",
    description: "Entre na sua conta para acessar o controle financeiro completo.",
    url: "/login"
  },
  onboarding: {
    title: "Configuração Inicial - Driver Finanças",
    description: "Configure seu perfil para começar a controlar suas finanças.",
    url: "/onboarding"
  }
};
